import {
  AreaChartOutlined,
  ClockCircleOutlined,
  DeleteFilled,
  FileProtectOutlined,
  InfoCircleOutlined,
  WarningFilled,
} from '@ant-design/icons'
import { useMetadata } from '@api/useMetadata'
import { useTankSupply } from '@api/useTankSupply'
import { defaultNumberDef } from '@components/_SiteWide/CellTemplates/typeColumnDefs'
import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { ApplyDefaultsDrawer } from '@components/Reports/TankSupplyDefaults/ApplyDefaultsDrawer/ApplyDefaultsDrawer'
import { BulkChangeBar } from '@components/Reports/TankSupplyDefaults/ApplyDefaultsDrawer/BulkChangeBar'
import { TankGridFilters } from '@components/Reports/TankSupplyDefaults/TankSupplyGrid/TankGridFilters'
import { addCommasToNumber, GraviGrid, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import api from '@utils/api'
import { Button, Space, Switch, Tooltip } from 'antd'
import classNames from 'classnames'
import moment from 'moment/moment'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useEffect } from 'react'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { useUserSettings } from '@hooks/settings'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tank-supply-defaults'

export function TankSupplyDefaults() {
  const [selectedStoreTanks, setSelectedStoreTanks] = useState([])
  const [filters, setFilters] = useState()
  const [isBulkChangeVisible, setIsBulkChangeVisible] = useState()

  const { useTankSupplyOverviewQuery, removeDefaultMutation, removeOverrideMutation, useTankSupplyMetadataQuery } =
    useTankSupply()
  const { data: tankSupplyData, isFetching, refetch } = useTankSupplyOverviewQuery(filters)
  const { mutateAsync: removeOverride } = removeOverrideMutation()
  const { mutateAsync: removeDefault } = removeDefaultMutation()
  const { data: tankSupplyMetadata } = useTankSupplyMetadataQuery()
  const gridAPIRef = useRef(null)

  useEffect(() => {
    // update selected rows with latest data when tankSupplyData changes
    if (selectedStoreTanks.length > 0 && tankSupplyData?.rows?.length > 0) {
      const updatedSelectedRows = selectedStoreTanks.map((selectedRow) => {
        const updatedRow = tankSupplyData.rows.find((row) => row.id === selectedRow.id) || selectedRow
        return updatedRow
      })
      setSelectedStoreTanks(updatedSelectedRows)
    }
  }, [tankSupplyData])

  const handleStoreTankChecked = (rows) => {
    setSelectedStoreTanks(rows)
  }
  const onDrawerSave = () => {
    setIsBulkChangeVisible(false)
    refetch().then(() => {
      gridAPIRef.current?.deselectAll()
      gridAPIRef.current?.resetRowHeights()
      gridAPIRef.current?.redrawRows()
    })
  }
  const { updateSetting } = useUserSettings()
  const columnDefs = useMemo(
    () => getColumnDefs(removeOverride, removeDefault, () => gridAPIRef?.current?.deselectAll(), selectedStoreTanks),
    [selectedStoreTanks]
  )

  const getRowId = useCallback((row) => row.data?.key, [])
  const agPropOverrides = useMemo(
    () => ({
      suppressRowClickSelection: true,
      isRowSelectable: (node) => {
        if (selectedStoreTanks.length === 0) {
          return true
        }
        return node.data ? node.data.product === selectedStoreTanks[0].product : false
      },
      rowSelection: 'multiple',
      groupSelectsFiltered: true,
      groupSelectsChildren: true,
      suppressCellSelection: true,
      rowMultiSelectWithClick: filters?.products?.length !== 1,
      enableRangeSelection: false,
      getRowId,
    }),
    [filters, selectedStoreTanks]
  )
  return (
    <Vertical className='pb-3'>
      <Vertical flex='1'>
        <GraviGrid
          onSelectionChanged={(props) => {
            handleStoreTankChecked(props.api.getSelectedRows(), props.api)
          }}
          externalRef={gridAPIRef}
          columnDefs={columnDefs}
          controlBarProps={{
            title: 'Tank Supply Defaults',
            actionButtons: (
              <>
                <Texto className={'mr-3'}>Only Carrier Managed Tanks: </Texto>
                <Switch
                  onChange={(checked) => {
                    setFilters({ ...filters, only_carrier_tanks: checked })
                  }}
                  className={'mr-5'}
                />
                <UploadDownloadButtons
                  downloadEP={() => api.getTankSupplyDownload()}
                  fileName='TankSupplyDefaults.xlsx'
                />
              </>
            ),
          }}
          loading={isFetching}
          agPropOverrides={agPropOverrides}
          storageKey={STORAGE_KEY}
          rowData={tankSupplyData?.rows}
          onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
          {...gridViewManager(STORAGE_KEY, updateSetting)}
        />
      </Vertical>
      <Vertical flex='0' style={{ minHeight: 70 }}>
        <BulkChangeBar setIsBulkChangeVisible={setIsBulkChangeVisible} selectedRows={selectedStoreTanks} />
      </Vertical>
      <ApplyDefaultsDrawer
        setIsBulkChangeVisible={setIsBulkChangeVisible}
        isBulkChangeVisible={isBulkChangeVisible}
        columnDefs={columnDefs}
        selectedRows={selectedStoreTanks}
        onDrawerSave={onDrawerSave}
      />
    </Vertical>
  )
}
const getColumnDefs = (removeOverride, removeDefault, deselectAll, selectedStoreTanks) => [
  {
    checkboxSelection: (params) => {
      if (!selectedStoreTanks.length) return true
      return selectedStoreTanks[0]?.product === params.data?.product
    },
    field: 'tank_supply_override',
    headerName: '',
    width: 110,
    flex: 0,
    enableRowGroup: false,
    valueGetter: ({ data }) => !data.tank_supply_default && !data.tank_supply_override,
    cellRenderer: ({ data }) => <NoSupplyIcon row={data} />,
  },
  {
    field: 'store_number',
    headerName: 'Site',
    width: 80,
    flex: 0,
    comparator: (a, b, nodeA, nodeB) => {
      const compareSettings = {
        numeric: true,
        sensitivity: 'base',
      }
      return nodeA?.data?.store_number.localeCompare(nodeB?.data?.store_number, undefined, compareSettings)
    },
    cellRenderer: ({ value }) => (
      <Texto category='h5' weight={600}>
        {value}
      </Texto>
    ),
  },

  {
    field: 'product',
    minWidth: 150,
    cellRenderer: ({ value }) => <Texto category='h6'>{value}</Texto>,
  },
  {
    field: 'daily_demand',
    headerName: 'Daily Demand',
    ...defaultNumberDef('daily_demand'),
    width: 200,
    flex: 0,
    valueFormatter: ({ value }) => `${addCommasToNumber(value)} GAL`,
  },
  {
    field: 'carrier',
    minWidth: 150,
  },
  {
    headerName: 'Assigned Supply',
    field: 'assigned_supply',
    minWidth: 400,
    autoHeight: true,
    flex: 1,
    valueGetter: ({ data }) => {
      const { tank_supply_default: defaultSupply, tank_supply_override: overrideSupply } = data
      const supply = overrideSupply || defaultSupply
      if (!overrideSupply && !defaultSupply) return null

      const { components, supply_zone, terminal } = supply
      if (!components.length && !supply_zone && !terminal) return null

      const bestRack = terminal || supply_zone
      const { terminal: componentTerminal, price_type, supplier } = components.length && components[0]
      return bestRack ? `${supply_zone || terminal} - BEST RACK` : `${componentTerminal} - ${price_type} - ${supplier}`
    },
    cellRenderer: ({ value, data }) => <AssignedSupplyCellRenderer value={value} row={data} />,
  },
  {
    headerName: 'Effective Until',
    enableRowGroup: false,
    sortable: false,
    menuTabs: [],
    field: 'effective_until',
    minWidth: 200,
    cellRenderer: ({ data }) => <SupplyEffectivesCellRenderer row={data} />,
  },
  {
    headerName: '',
    suppressRowClickSelection: true,
    key: 'clear_default_supply',

    maxWidth: 60,
    cellRenderer: ({ data }) => (
      <ClearDefaultSupplyRenderer
        row={data}
        removeDefault={removeDefault}
        removeOverride={removeOverride}
        deselectAll={deselectAll}
      />
    ),
  },
]
function ClearDefaultSupplyRenderer({ row, removeOverride, removeDefault, deselectAll }) {
  const { tank_supply_default, tank_supply_override } = row
  if (!tank_supply_default && !tank_supply_override) return null
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState()
  return (
    <>
      {tank_supply_default && (
        <Button
          ghost
          className='delete-tank-default'
          icon={<DeleteFilled style={{ color: 'var(--theme-error)' }} />}
          loading={isLoading}
          onClick={async (e) => {
            e.preventDefault()
            e.stopPropagation()
            setIsLoading(true)
            await removeDefault([row])
            queryClient.invalidateQueries({
              queryKey: ['store/tank/supply/metadata'],
            })
            setIsLoading(false)
            deselectAll()
          }}
        />
      )}
      {tank_supply_override && (
        <Button
          ghost
          className='delete-tank-default'
          icon={<DeleteFilled style={{ color: 'var(--theme-error)' }} />}
          loading={isLoading}
          onClick={async (e) => {
            e.stopPropagation()
            e.preventDefault()
            setIsLoading(true)
            await removeOverride([row])
            queryClient.invalidateQueries({
              queryKey: ['store/tank/supply/metadata'],
            })
            setIsLoading(false)
            deselectAll()
          }}
        />
      )}
    </>
  )
}
function NoSupplyIcon({ row }) {
  const { tank_supply_default: tankDefault, tank_supply_override: tankOverride } = row
  if (!!tankDefault || !!tankOverride) return null
  return (
    <div>
      <Tooltip title='No Supply Defined'>
        <Texto className='pl-2' category='h3' appearance='error'>
          <WarningFilled />
        </Texto>
      </Tooltip>
    </div>
  )
}

function SupplyEffectivesCellRenderer(row) {
  const { tank_supply_default: tankDefault, tank_supply_override: tankOverride } = row.row
  if (!tankDefault && !tankOverride) return null
  const from = formatters.DATE(moment(tankOverride?.effective_from))
  const to = formatters.DATE(moment(tankOverride?.effective_to))
  const effectiveRange = `${from} - ${to}`
  const now = moment()
  const overrideIsActive = now.isAfter(from) && now.isBefore(to)
  const overrideColor = overrideIsActive ? '' : 'var(--gray-500)'
  const defaultColor = tankOverride && (overrideIsActive ? 'var(--gray-500)' : '')

  return (
    <Vertical>
      {tankDefault && (
        <Texto className='flex items-center' category='h6' appearance='secondary'>
          <div style={{ color: defaultColor }}>
            <ClockCircleOutlined className='pr-2' /> Forever
          </div>
        </Texto>
      )}
      <Vertical flex={1}></Vertical>
      {tankOverride && (
        <Texto className='flex items-center' category='h6' appearance='secondary'>
          <div style={{ color: overrideColor }}>
            <ClockCircleOutlined className='pr-2' /> {effectiveRange}
          </div>
        </Texto>
      )}
    </Vertical>
  )
}

function AssignedSupplyCellRenderer({ row }) {
  const { tank_supply_default: defaultSupply, tank_supply_override: overrideSupply } = row
  if (!defaultSupply && !overrideSupply) {
    return (
      <div className='flex-div'>
        <Space>
          <Texto appearance='medium'>
            <InfoCircleOutlined />
          </Texto>

          <Texto appearance='medium'>No Supply Defined</Texto>
        </Space>
      </div>
    )
  }

  return (
    <div className='h-100'>
      {defaultSupply && <AssignedSupplySubRow supply={defaultSupply} isActive={!overrideSupply} />}
      {overrideSupply && (
        <div className='mt-3'>
          <AssignedSupplySubRow supply={overrideSupply} isActive />
        </div>
      )}
    </div>
  )
}

function AssignedSupplySubRow({ supply, isActive }) {
  const { components, supply_zone, terminal } = supply
  if (!components.length && !supply_zone && !terminal) return null

  const bestRack = terminal || supply_zone
  const { terminal: componentTerminal, price_type, supplier } = components.length && components[0]
  const supplyDescription = bestRack
    ? `${supply_zone || terminal} - BEST RACK`
    : `${componentTerminal} - ${price_type} - ${supplier}`

  return (
    <div className=' flex  text-ellipsis'>
      <div className='vertical-flex-center bg-3 supply-icon-container'>
        {bestRack ? (
          <div
            className={classNames(
              'supply-icon flex-1 vertical-flex-center items-center',
              { 'bg-success': isActive },
              { 'bg-2': !isActive }
            )}
          >
            <AreaChartOutlined style={{ color: isActive ? 'var(--gray-100)' : 'var(--gray-500)' }} />
          </div>
        ) : (
          <div
            className={classNames(
              'supply-icon flex-1 vertical-flex-center items-center',
              { 'bg-theme2': isActive },
              { 'bg-2': !isActive }
            )}
          >
            <FileProtectOutlined style={{ color: isActive ? 'var(--gray-100)' : 'var(--gray-500)' }} />
          </div>
        )}
      </div>
      <div className='pl-3 pr-4 py-1 bg-3 vertical-flex-center h-100 supply-tag'>
        <Texto category='p1' weight='bold'>
          {supplyDescription}
        </Texto>
      </div>
    </div>
  )
}
